import type {CSSProperties} from 'react';
import {useMemo} from 'react';

import type {Size} from '#theme';
import {
  Color,
  getBaseColor,
  getColorStyles,
  getVariantColors,
  isVariantColor,
  sizeVarMap,
} from '#theme';
import {getValueByHash} from '#utils/arrays.js';

import {
  DEFAULT_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_ICON_SIZE,
  DEFAULT_NAME,
  DEFAULT_SIZE,
  EXCLUDED_AVATAR_COLORS,
} from './constants.js';
import type {AvatarStyle, useAvatarStylesProps} from './types.js';

/**
 * Get the chosen color for the avatar.
 * @param name - The name of the avatar.
 * @param color - The color of the avatar.
 * @param randomize - Whether to randomize the color of the avatar.
 * @returns The chosen color for the avatar.
 */
function getChosenColor(name: string, color: Color, randomize: boolean) {
  const colorsList = getAvatarColors();
  if (randomize && name) {
    return getValueByHash(colorsList, name);
  }
  return color;
}

/**
 * Get the nearest size map value.
 * @param size - The size of the avatar.
 * @param multiplier - The multiplier for the size.
 * @returns The nearest size map value.
 */
function getNearestSizeMapValue(size: number, multiplier = 1) {
  return sizeVarMap[Math.max(1, Math.floor(size * multiplier)) as Size];
}

/**
 * Calculate the sizes for the avatar.
 * @param size - The size of the avatar.
 * @param style - The style of the avatar.
 * @returns The sizes for the avatar.
 */
function calculateSizes(size: Size, style?: AvatarStyle) {
  const defaultAvatarSize = getNearestSizeMapValue(size, 1);

  const avatarSize = style?.['--size'] ?? defaultAvatarSize;
  const iconSize = style?.['--iconSize'] ?? DEFAULT_ICON_SIZE;
  const fontSize = style?.['--fontSize'] ?? DEFAULT_FONT_SIZE;

  return {
    avatarSize,
    iconSize,
    fontSize,
  };
}

/**
 * Get the avatar approved colors.
 * @returns The avatar approved colors.
 */
export function getAvatarColors() {
  return Object.values(Color).filter(
    color => !EXCLUDED_AVATAR_COLORS.includes(color)
  );
}

/**
 * Use the avatar styles.
 * @param props - The props for the avatar.
 * @returns The styles and classes for the avatar root.
 */
export function useAvatarStyles({
  name = DEFAULT_NAME,
  size = DEFAULT_SIZE,
  color = DEFAULT_COLOR,
  randomize = false,
  style,
}: useAvatarStylesProps) {
  const chosenColor = useMemo(
    () => getChosenColor(name, color, randomize),
    [randomize, name, color]
  );

  const fontColor = chosenColor;
  const bgColor = isVariantColor(fontColor)
    ? getBaseColor(fontColor)
    : getVariantColors(fontColor)[0].variant;

  const rootStyles = useMemo(() => {
    const {avatarSize, iconSize, fontSize} = calculateSizes(size, style);
    const rootStyles = {
      '--avatar-size': avatarSize,
      '--font-size': fontSize,
      '--icon-size': iconSize,
      '--bg-color': getColorStyles({bg: bgColor}).backgroundColor,
      '--font-color': getColorStyles({color: fontColor}).color,
    };
    return rootStyles as CSSProperties;
  }, [size, style, bgColor, fontColor]);

  return rootStyles;
}
